/**
 * Blocking notice shown when this window belongs to a different account than the
 * one now signed in (#871).
 *
 * Rendered ONCE PER LIVE DOCUMENT — the main page renders one, and `PopoutFrame`
 * renders one inside each detached window — because a portalled node lands in a
 * single document and a clinician may be looking only at a popped-out viewer.
 *
 * Blocking, not a dismissible banner: every control on the page now belongs to an
 * account the user is not, and the identity gate will refuse those writes anyway,
 * so letting the user keep clicking only produces refusals.
 *
 * Never reloads on its own. A viewer holds unsaved work (annotations in progress,
 * view state), and discarding it without asking would turn a warning into data
 * loss. The user takes the action.
 */

import { useContext, useEffect, useState } from 'preact/hooks';
import { onSessionStale, staleReason, type StaleReason } from '@brainfocus/web/services/session_identity';
import { PopoutDocumentContext } from '@brainfocus/web/shared/platform/popout_context';
import {
    SESSION_CHANGED_LEAD,
    SESSION_CHANGED_MESSAGE,
    SESSION_SIGNED_OUT_LEAD,
    SESSION_SIGNED_OUT_MESSAGE,
} from '@brainfocus/web/api/client';
import './stale_session_notice.scss';

/**
 * Page reload, behind an indirection so the suite can assert it is NOT called
 * without the user's action. jsdom has no navigation, so calling the real thing
 * in a test would print "Not implemented: navigation".
 */
let reload: (() => void) | null = null;

function doReload(): void {
    if (reload) {
        reload();
        return;
    }
    window.location.reload();
}

/** Test seam only — never call from app code. Pass `null` to restore. */
export function setReloadForTests(fn: (() => void) | null): void {
    reload = fn;
}

/**
 * Subscribe a component to the stale flag.
 *
 * The initial value is read from the store rather than assumed `false`: a section
 * detached AFTER the identity changed mounts into an already-stale app and must
 * show the notice immediately.
 */
export function useStaleSession(): StaleReason | null {
    const [reason, setReason] = useState(staleReason);
    useEffect(() => {
        const current = staleReason();
        if (current) {
            // Became stale between render and effect.
            setReason(current);
            return undefined;
        }
        return onSessionStale(() => setReason(staleReason()));
    }, []);
    return reason;
}

/**
 * What to tell the user, per case. A sign-out is not "a different account is
 * signed in" — there is no account to name — and saying so was wrong, which #871
 * QA caught.
 */
const MESSAGE: Record<StaleReason, string> = {
    'other-user': SESSION_CHANGED_MESSAGE,
    'signed-out': SESSION_SIGNED_OUT_MESSAGE,
};

/**
 * What the recovery does when the notice is read inside a detached window.
 *
 * A detached window carries no page of its own — its content is rendered from
 * the main window — so it cannot reload itself, and `usePopoutWindow` takes the
 * detached windows down with the main one on purpose (#791), so that a reload
 * never leaves an orphaned viewer behind. Promising to "reload the page" from
 * here is therefore a promise the action does not keep: PR #896 QA pressed it
 * and watched the window close. It says what happens instead.
 */
export const DETACHED_RELOAD_NOTE = 'Reloading will close this window.';

/**
 * The same two cases, told from inside a detached window: what happened, then
 * what pressing the button does here. One sentence each rather than the main
 * window's message plus a second paragraph — the earlier three-line version said
 * more than the action warrants (Carlota, PR #896 review).
 */
const DETACHED_MESSAGE: Record<StaleReason, string> = {
    'other-user': `${SESSION_CHANGED_LEAD} ${DETACHED_RELOAD_NOTE}`,
    'signed-out': `${SESSION_SIGNED_OUT_LEAD} ${DETACHED_RELOAD_NOTE}`,
};

export function StaleSessionNotice() {
    const reason = useStaleSession();
    // The notice is rendered once per live document; inside a detached window
    // `PopoutFrame` provides this from within its own portal, so only that
    // subtree sees it.
    const detached = useContext(PopoutDocumentContext) !== null;
    if (!reason) {
        return null;
    }
    return (
        <div class='bf-stale-session-backdrop' role='alertdialog' aria-modal='true'>
            <div class='bf-stale-session-content'>
                <p class='bf-stale-session-message'>
                    {detached ? DETACHED_MESSAGE[reason] : MESSAGE[reason]}
                </p>
                <button class='bf-stale-session-reload' onClick={doReload}>
                    Reload
                </button>
            </div>
        </div>
    );
}
